import dagre from 'dagre'
import type { Edge } from '@xyflow/react'
import type { PipelineNode } from '../types'

const NODE_WIDTH = 160
const NODE_HEIGHT = 80
const HORIZONTAL_GAP = 40
const VERTICAL_GAP = 60

export interface LayoutOptions {
  direction?: 'LR' | 'TB'
  nodeWidth?: number
  nodeHeight?: number
}

export function getLayoutedElements(
  nodes: PipelineNode[],
  edges: Edge[],
  options: LayoutOptions = {}
): { nodes: PipelineNode[]; edges: Edge[] } {
  const {
    direction = 'LR',
    nodeWidth = NODE_WIDTH,
    nodeHeight = NODE_HEIGHT,
  } = options

  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))

  const isHorizontal = direction === 'LR'
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: isHorizontal ? VERTICAL_GAP : HORIZONTAL_GAP,
    ranksep: isHorizontal ? HORIZONTAL_GAP : VERTICAL_GAP,
    marginx: 20,
    marginy: 20,
  })

  // Add nodes to dagre
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight })
  })

  // Add edges to dagre
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target)
  })

  // Run layout algorithm
  dagre.layout(dagreGraph)

  // Apply positions back to nodes
  const layoutedNodes: PipelineNode[] = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id)
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    }
  })

  return { nodes: layoutedNodes, edges }
}

// Grid-based layout for n8n-style display
export function getGridLayout(
  nodes: PipelineNode[],
  edges: Edge[]
): { nodes: PipelineNode[]; edges: Edge[] } {
  const GRID_CELL_WIDTH = NODE_WIDTH + HORIZONTAL_GAP
  const GRID_CELL_HEIGHT = NODE_HEIGHT + VERTICAL_GAP
  const MAX_COLS = 4
  const PADDING = 40

  const layoutedNodes: PipelineNode[] = nodes.map((node, index) => {
    const row = Math.floor(index / MAX_COLS)
    const col = index % MAX_COLS

    return {
      ...node,
      position: {
        x: PADDING + col * GRID_CELL_WIDTH,
        y: PADDING + row * GRID_CELL_HEIGHT,
      },
    }
  })

  return { nodes: layoutedNodes, edges }
}
